import { CodeCheckService } from '@lemon/core/service/code-check.service'
import { LaskuToimittaja, LaskuToimittajaIban, ToimittajaTunniste } from '../../model/lasku'
import { VatNumberValidationService } from '../vat-number-validation.service'

export interface ToimittajanTunnistekentat {
  tunnisteetData: ToimittajaTunniste[]
  tunnisteet: string[]
  ytunnus: string | null
}

export interface ToimittajanTaydennys extends ToimittajanTunnistekentat {
  iban: { [iban: string]: LaskuToimittajaIban }
  /** Toivatko lisättävät arvot uutta tietoa (kirjoitetaanko toimittaja). */
  muuttui: boolean
}

export class ToimittajaTunnisteService {
  constructor(
    private _codeCheckService: CodeCheckService,
    private _vatNumberValidationService: VatNumberValidationService,
  ) {}

  private _siivoaSailytaKirjainkoko(arvo: string): string {
    return (arvo ?? '').trim()
  }

  private _siivoa(arvo: string): string {
    return this._siivoaSailytaKirjainkoko(arvo).toUpperCase()
  }

  luokitteleTunniste(arvo: string): ToimittajaTunniste | null {
    const siivottu = this._siivoa(arvo)
    if (!siivottu) {
      return null
    }
    if (this._codeCheckService.isValidYTunnus(siivottu)) {
      return { tyyppi: 'y-tunnus', arvo: siivottu }
    }
    if (this._codeCheckService.isValidVatNumber(siivottu)) {
      return { tyyppi: 'vat', arvo: siivottu, maakoodi: 'FI' }
    }
    const vatTulos = this._vatNumberValidationService.checkVAT(siivottu)
    if (vatTulos.isValid && vatTulos.country?.code && vatTulos.country.code !== 'FI') {
      return { tyyppi: 'vat', arvo: siivottu, maakoodi: vatTulos.country.code }
    }
    // Tuntematon tunniste: säilytetään alkuperäinen kirjainkoko (vain y-tunnus/vat isoiksi kirjaimiksi).
    return { tyyppi: 'muu', arvo: this._siivoaSailytaKirjainkoko(arvo) }
  }

  /** Suomalaiselle tunnisteelle molemmat muodot (y-tunnus + vat), muuten tunniste sellaisenaan. */
  private _tunnisteMolemmatSuomalaisetMuodot(arvo: string): ToimittajaTunniste[] {
    const tunniste = this.luokitteleTunniste(arvo)
    if (!tunniste) {
      return []
    }
    if (tunniste.tyyppi === 'y-tunnus') {
      const vat = this._codeCheckService.annaVatTunnus(tunniste.arvo)
      return [tunniste, { tyyppi: 'vat', arvo: vat, maakoodi: 'FI' }]
    }
    if (tunniste.tyyppi === 'vat' && tunniste.maakoodi === 'FI') {
      const ytunnus = this._codeCheckService.annaYTunnus(tunniste.arvo)
      return [tunniste, { tyyppi: 'y-tunnus', arvo: ytunnus }]
    }
    return [tunniste]
  }

  /** Muodostaa toimittajan tallennettavat tunnistekentät vastaanotetuista raakatunnisteista. Idempotentti: aja tallennuksessa aina samasta lähteestä. */
  muodostaToimittajanTunnisteet(arvot: string[]): ToimittajanTunnistekentat {
    const tunnisteetData: ToimittajaTunniste[] = []
    for (const arvo of arvot) {
      for (const tunniste of this._tunnisteMolemmatSuomalaisetMuodot(arvo)) {
        if (!tunnisteetData.some(t => t.arvo === tunniste.arvo)) {
          tunnisteetData.push(tunniste)
        }
      }
    }

    return this.muodostaTunnistekentatDatasta(tunnisteetData)
  }

  /** Johtaa hakuindeksin ja ensisijaisen tunnisteen valmiista tunnisteetDatasta. */
  muodostaTunnistekentatDatasta(tunnisteetData: ToimittajaTunniste[]): ToimittajanTunnistekentat {
    // Indeksiin tulee jokainen tunnistemuoto: sekä alkuperäinen syötetty että suomalaisen komplementti (esim. FI12345678 JA 1234567-8), jotta array-contains osuu kummalla tahansa muodolla haettaessa.
    const tunnisteet = [...new Set(tunnisteetData.map(t => t.arvo))]

    return { tunnisteetData, tunnisteet, ytunnus: tunnisteetData[0]?.arvo ?? null }
  }

  private _onSuomalainen(tunniste: ToimittajaTunniste): boolean {
    if (tunniste.tyyppi === 'y-tunnus') {
      return true
    }
    return tunniste.tyyppi === 'vat' && (tunniste.maakoodi === 'FI' || this._codeCheckService.isValidVatNumber(this._siivoa(tunniste.arvo)))
  }

  private _suomalainenYTunnuksena(tunniste: ToimittajaTunniste): string {
    return tunniste.tyyppi === 'y-tunnus' ? tunniste.arvo : this._codeCheckService.annaYTunnus(this._siivoa(tunniste.arvo))
  }

  /** Yksittäinen raaka-arvo näytettävään muotoon: suomalainen vat y-tunnukseksi, muu sellaisenaan. */
  naytettavaTunnisteArvosta(arvo: string | null | undefined): string | null {
    const siivottu = this._siivoaSailytaKirjainkoko(arvo)
    if (!siivottu) {
      return null
    }
    const isoilla = siivottu.toUpperCase()
    return this._codeCheckService.isValidVatNumber(isoilla) ? this._codeCheckService.annaYTunnus(isoilla) : siivottu
  }

  /** Kuittipuolella näytettävä tunniste: suomalainen aina y-tunnusmuodossa, muuten ensimmäinen tunniste sellaisenaan. */
  annaNaytettavaTunniste(tunnisteetData: ToimittajaTunniste[] | undefined, ensisijainen?: string | null): string | null {
    const data = tunnisteetData ?? []
    const suomalainen = data.find(t => t.tyyppi === 'y-tunnus') ?? data.find(t => this._onSuomalainen(t))
    if (suomalainen) {
      return this._suomalainenYTunnuksena(suomalainen)
    }
    if (data.length) {
      return data[0].arvo
    }
    return this.naytettavaTunnisteArvosta(ensisijainen)
  }

  /** Vanhoilla toimittajilla voi olla pelkkä ensisijainen tunniste ilman tunnisteetDataa. */
  nykyisetTunnisteet(toimittaja: Pick<LaskuToimittaja, 'tunnisteetData' | 'ytunnus'>): ToimittajaTunniste[] {
    if (toimittaja.tunnisteetData?.length) {
      return toimittaja.tunnisteetData
    }
    return this.muodostaToimittajanTunnisteet(toimittaja.ytunnus ? [toimittaja.ytunnus] : []).tunnisteetData
  }

  /** Käyttäjän muokattavissa oleva tunniste: suomalainen y-tunnusmuodossa, muuten ensimmäinen ALV-tunnus, muuten null. */
  annaMuokattavaTunniste(tunnisteetData: ToimittajaTunniste[]): string | null {
    const data = tunnisteetData ?? []
    const suomalainen = data.find(t => t.tyyppi === 'y-tunnus') ?? data.find(t => this._onSuomalainen(t))
    if (suomalainen) {
      return this._suomalainenYTunnuksena(suomalainen)
    }
    return data.find(t => t.tyyppi === 'vat')?.arvo ?? null
  }

  private _tunnisteenMuodot(data: ToimittajaTunniste[], arvo: string | null): ToimittajaTunniste[] {
    if (!arvo) {
      return []
    }
    const muodot = new Set<string>([arvo])
    for (const tunniste of this._tunnisteMolemmatSuomalaisetMuodot(arvo)) {
      muodot.add(tunniste.arvo)
    }
    return data.filter(t => muodot.has(t.arvo) || muodot.has(this._siivoa(t.arvo)))
  }

  private _muokattavanMuodot(data: ToimittajaTunniste[]): ToimittajaTunniste[] {
    return this._tunnisteenMuodot(data, this.annaMuokattavaTunniste(data))
  }

  private _korvaaMuodot(data: ToimittajaTunniste[], korvattavat: ToimittajaTunniste[], uusi: string | null): ToimittajaTunniste[] {
    const jaljelle = data.filter(t => !korvattavat.includes(t))
    const uudet = this._tunnisteMolemmatSuomalaisetMuodot(uusi ?? '')
    const ennallaan = uudet.length > 0 && korvattavat.length > 0 && uudet.every(u => korvattavat.some(t => t.arvo === u.arvo))
    if (ennallaan) {
      return [...data]
    }
    return [...uudet, ...jaljelle.filter(t => !uudet.some(u => u.arvo === t.arvo))]
  }

  /**
   * Korvaa kuittipuolella näytettävän tunnisteen uudella: poistaa vain näytetyn (suomalaisen parin tai
   * ensimmäisen tunnisteen) ja lisää uuden ensimmäiseksi, jolloin siitä tulee ensisijainen. Muut tunnisteet säilyvät.
   */
  vaihdaNaytettavaTunniste(tunnisteetData: ToimittajaTunniste[], uusi: string | null): ToimittajaTunniste[] {
    const data = tunnisteetData ?? []
    return this._korvaaMuodot(data, this._tunnisteenMuodot(data, this.annaNaytettavaTunniste(data)), uusi)
  }

  /** Muokattavan tunnisteen ulkopuoliset tunnisteet näytettävässä muodossa (vain luku). Suomalaisen tunnisteen ALV-muotoa ei toisteta. */
  annaMuutTunnisteet(tunnisteetData: ToimittajaTunniste[]): string[] {
    const data = tunnisteetData ?? []
    const muokattavanMuodot = this._muokattavanMuodot(data)
    const muut: string[] = []
    for (const tunniste of data) {
      if (muokattavanMuodot.includes(tunniste)) {
        continue
      }
      const naytettava = this._onSuomalainen(tunniste) ? this._suomalainenYTunnuksena(tunniste) : tunniste.arvo
      if (!muut.includes(naytettava)) {
        muut.push(naytettava)
      }
    }
    return muut
  }

  /**
   * Korvaa muokattavan tunnisteen (suomalainen pari tai ALV-tunnus) uudella ja lisää sen ensimmäiseksi, jolloin siitä
   * tulee ensisijainen. Muut tunnisteet säilyvät aina, myös kun uusi on tyhjä.
   */
  vaihdaMuokattavaTunniste(tunnisteetData: ToimittajaTunniste[], uusi: string | null): ToimittajaTunniste[] {
    const data = tunnisteetData ?? []
    return this._korvaaMuodot(data, this._muokattavanMuodot(data), uusi)
  }

  /**
   * Vanhojen mobiiliversioiden tunnistelista: korvaa suomalaiset tunnisteet listan tunnisteilla, mutta säilyttää
   * listasta puuttuvat ulkomaiset ja muut tunnisteet, koska kuittipuoli ei näytä niitä eikä saa hävittää niitä.
   */
  korvaaSuomalaisetTunnisteet(tunnisteetData: ToimittajaTunniste[], arvot: string[]): ToimittajaTunniste[] {
    const uudet = this.muodostaToimittajanTunnisteet(arvot).tunnisteetData
    const sailytettavat = (tunnisteetData ?? []).filter(t => !this._onSuomalainen(t) && !uudet.some(u => u.arvo === t.arvo))
    return [...uudet, ...sailytettavat]
  }

  /**
   * Laskee toimittajan täydennetyt tunniste- ja iban-kentät uusilla arvoilla. Palauttaa uudet kentät ja tiedon muuttuiko mikään.
   * IBAN lisätään jos se ei ole jo toimittajalla ensimmäinen IBAN saa tyypin 'oletus', muut 'tavallinen'.
   * Tunnisteet vain kasvavat (ei koskaan poisteta).
   */
  taydennaToimittaja(
    nykyinen: Pick<LaskuToimittaja, 'tunnisteetData' | 'tunnisteet' | 'iban'>,
    lisattavatTunnisteArvot: string[],
    lisattavaIban?: string,
    lisattavaBic?: string,
  ): ToimittajanTaydennys {
    const iban = { ...(nykyinen.iban ?? {}) }
    let ibanMuuttui = false
    if (lisattavaIban && !iban[lisattavaIban]) {
      const tyyppi = Object.keys(iban).length === 0 ? 'oletus' : 'tavallinen'
      iban[lisattavaIban] = { iban: lisattavaIban, bic: lisattavaBic ?? '', tyyppi }
      ibanMuuttui = true
    }

    const nykyisetArvot = nykyinen.tunnisteetData.map(t => t.arvo)
    const yhdistetyt = this.muodostaToimittajanTunnisteet([...nykyisetArvot, ...lisattavatTunnisteArvot])
    const tunnisteetMuuttui = yhdistetyt.tunnisteet.length !== nykyinen.tunnisteet.length

    return { ...yhdistetyt, iban, muuttui: ibanMuuttui || tunnisteetMuuttui }
  }
}
